import { CheckCircleIcon, DeviceMobileIcon, SquaresFourIcon } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/cms/ui/input';
import { Field, SubsectionTitle } from '@/modules/primitives3D/editorShell';
import { FieldHint, FieldLabel, SectionTitle } from '@/components/ui/text';
import { ListRow } from '@/components/ui/list-row';
import { Tile } from '@/components/ui/tile';
import { Segmented } from '@/components/ui/segmented';
import { TextButton, TextLink } from '@/components/ui/text-button';
import { ChannelIcon } from '@/modules/sceneHealth';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { DEVICE_DESCRIPTIONS, DEVICE_LABELS, getDeviceAvailability } from '../deviceAvailability';
import { ERROR_INPUT_CLASS, creatorNameError, storyNameError, type FieldKey } from '../validation';
import { FieldError, RequiredMark } from '../FieldMarks';
import { PillButton } from '../PillButton';
import { TEMPLATE_INTEREST_URL } from '../templateOptions';
import { VERSION_STATUS_LABEL } from '../versionOptions';
import { VISIBILITY_OPTIONS, VISIBILITY_SWITCH_HINT } from '../visibilityOptions';
import type { AnchorCategory, PublishDestination, PublishDevice, PublishVersionOption } from '../types';
import type { ChosenVisibility } from '@/infrastructure/api/v1/mappers/stories';

interface Props {
  storyName: string;
  creatorName: string;
  anchorCategory: AnchorCategory | undefined;
  devices: PublishDevice[];
  errors: Set<FieldKey>;
  sceneHealthBlockedReasons?: Map<PublishDevice, string>;
  versions: PublishVersionOption[];
  selectedVersionId: string | null;
  versionLocked: boolean;
  showStoryName?: boolean;
  // The template group renders only when the flow supports it. The project-level
  // publish flow shares this step and has no template destination.
  destination?: PublishDestination;
  // Set when the story already has a template the creator can't replace.
  templateBlockedReason?: string;
  // The acceptance requirements this story misses. The tile stays pickable, so this
  // only arrives once the creator has tried to move past this step.
  templateRequirementsWarning?: string | null;
  onSelectTemplateDestination?: () => void;
  onRegisterTemplateInterest?: () => void;
  // Opens the in-editor test (share/QR) dialog. Only surfaced for place-anywhere
  // scenes, which can't be published but can still be tested.
  onTest?: () => void;
  onSelectVersion: (sceneInstanceId: string) => void;
  onToggleDevice: (d: PublishDevice) => void;
  onStoryNameChange: (v: string) => void;
  onCreatorNameChange: (v: string) => void;
  onRequestImmersalToken?: () => void;
  visibility?: ChosenVisibility;
  onVisibilityChange?: (v: ChosenVisibility) => void;
}

const DEVICE_ORDER: PublishDevice[] = ['app', 'appless', 'spectacles'];

const DEVICE_TO_ICON_TYPE: Record<PublishDevice, 'phone' | 'globe' | 'snap'> = {
  app: 'phone',
  appless: 'globe',
  spectacles: 'snap',
};

export function ChoosePlatformStep({
  storyName,
  creatorName,
  anchorCategory,
  devices,
  errors,
  sceneHealthBlockedReasons,
  versions,
  selectedVersionId,
  versionLocked,
  showStoryName = true,
  destination = 'audience',
  templateBlockedReason,
  templateRequirementsWarning,
  onSelectTemplateDestination,
  onRegisterTemplateInterest,
  onTest,
  onSelectVersion,
  onToggleDevice,
  onStoryNameChange,
  onCreatorNameChange,
  onRequestImmersalToken,
  visibility,
  onVisibilityChange,
}: Props) {
  const services = useServices();
  const { subscriptionPlan, orgInstance, orgMember } = useOrganization();
  const canUseAppless = services.subscriptionPlan.canUseApplessPlatform(subscriptionPlan ?? '');
  const availability = getDeviceAvailability(anchorCategory, { canUseAppless });
  const isPlaceAnywhere = anchorCategory === 'place_anywhere';
  const isImmersal = anchorCategory === 'immersal';
  const needsImmersalToken =
    isImmersal && Boolean(onRequestImmersalToken) && !orgInstance?.orgInstanceImmersalConfigured;

  const showTemplateGroup = Boolean(onSelectTemplateDestination);
  const templateSelected = destination === 'template';
  const eligibleForTemplates = services.subscriptionPlan.canCreateTemplateScenes(
    subscriptionPlan ?? '',
    orgMember,
  );
  const canPublishTemplates = eligibleForTemplates && !templateBlockedReason;

  const handleToggleDevice = (d: PublishDevice) => {
    const selecting = !devices.includes(d) || templateSelected;
    onToggleDevice(d);
    if (d === 'appless' && selecting && needsImmersalToken) {
      onRequestImmersalToken?.();
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full flex-col">
      <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line flex-1 min-h-0">
        <div className="space-y-4 min-w-0 pr-7">
          <SectionTitle>Details</SectionTitle>

          {showStoryName && (
            <Field>
              <FieldLabel mb>
                Story name
                {!templateSelected && <RequiredMark />}
              </FieldLabel>
              <Input
                className={errors.has('storyName') && !templateSelected ? ERROR_INPUT_CLASS : undefined}
                value={storyName}
                onChange={(e) => onStoryNameChange(e.target.value)}
                placeholder="Untitled story"
                disabled={templateSelected}
              />
              {templateSelected ? (
                <FieldHint>Your template gets its own name in the next step.</FieldHint>
              ) : (
                errors.has('storyName') && <FieldError>{storyNameError(storyName)}</FieldError>
              )}
            </Field>
          )}

          <Field>
            <FieldLabel mb>
              Creator name
              <RequiredMark />
            </FieldLabel>
            <Input
              className={errors.has('creatorName') ? ERROR_INPUT_CLASS : undefined}
              value={creatorName}
              onChange={(e) => onCreatorNameChange(e.target.value)}
              placeholder="Your name"
            />
            {errors.has('creatorName') && <FieldError>{creatorNameError(creatorName)}</FieldError>}
          </Field>

          {versions.length > 0 && (
            <Field>
              <FieldLabel mb>Story version</FieldLabel>
              <div className="space-y-1.5">
                {versions.map((v) => {
                  const isSelected = v.sceneInstanceId === selectedVersionId;
                  const interactive = !versionLocked && v.selectable && versions.length > 1;
                  const row = (
                    <ListRow
                      disabled={!v.selectable}
                      selected={isSelected}
                      onClick={() => interactive && onSelectVersion(v.sceneInstanceId)}
                      label={
                        <>
                          {v.versionNumber != null && <span className="mr-2 font-semibold">v{v.versionNumber}</span>}
                          {v.label}
                        </>
                      }
                      trailing={
                        <>
                          {v.disabledReason ?? VERSION_STATUS_LABEL[v.status]}
                          {isSelected && <CheckCircleIcon weight="fill" className="h-4 w-4 text-lureo-brand-ink" />}
                        </>
                      }
                    />
                  );
                  if (interactive || v.selectable || !v.disabledReason) return <div key={v.sceneInstanceId}>{row}</div>;
                  return (
                    <Tooltip key={v.sceneInstanceId}>
                      <TooltipTrigger asChild>
                        <span className="block w-full">{row}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>{v.disabledReason}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </Field>
          )}
        </div>

        <div className="relative min-w-0 pl-7">
          {/* Who can find it, on the heading's line: both choices named, Public by default, its meaning right under it */}
          <div className="mb-1 flex items-center justify-between gap-3">
            <SectionTitle className="mb-0">{showTemplateGroup ? 'Destination' : 'Platform'}</SectionTitle>
            {!templateSelected && visibility && onVisibilityChange && (
              <Segmented variant="pills" label="Who can find it" options={VISIBILITY_OPTIONS} value={visibility} onChange={onVisibilityChange} />
            )}
          </div>

          {/* The choice's meaning right under it */}
          {(showTemplateGroup || (!templateSelected && visibility && onVisibilityChange)) && (
            <div className="flex items-baseline justify-between gap-3">
              {showTemplateGroup ? <SubsectionTitle>Publish as Experience</SubsectionTitle> : <span />}
              {!templateSelected && visibility && onVisibilityChange && (
                <FieldHint className="mt-0 mb-2 text-right">{VISIBILITY_SWITCH_HINT[visibility]}</FieldHint>
              )}
            </div>
          )}

          <div
            className={`grid grid-cols-3 gap-2 ${showTemplateGroup ? '' : 'mt-3'} ${
              templateSelected ? 'opacity-lureo-disabled-opacity' : ''
            }`}
          >
            {DEVICE_ORDER.map((d) => {
              const a = availability[d];
              const selected = !templateSelected && devices.includes(d);
              const sceneReason = sceneHealthBlockedReasons?.get(d);
              const disabled = a.state === 'disabled' || sceneReason != null;
              const tooltipText = disabled
                ? (sceneReason ?? a.disabledReason ?? '')
                : DEVICE_DESCRIPTIONS[d];
              const card = (
                <Tile
                  label={DEVICE_LABELS[d]}
                  glyph={<ChannelIcon type={DEVICE_TO_ICON_TYPE[d]} className="h-7 w-7" />}
                  selected={selected}
                  disabled={disabled}
                  onClick={() => handleToggleDevice(d)}
                  className="aspect-square"
                />
              );
              return (
                <Tooltip key={d}>
                  <TooltipTrigger asChild>
                    <span className="block w-full">{card}</span>
                  </TooltipTrigger>
                  {tooltipText && (
                    <TooltipContent side="bottom" sideOffset={8}>
                      {tooltipText}
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </div>

          {!templateSelected && devices.includes('appless') && !devices.includes('app') && (
            <FieldHint className="mt-2">
              If the viewer has the Lureo app, this story will open directly in the app for a better experience.
            </FieldHint>
          )}

          {showTemplateGroup && (
            <div className="mt-5">
              <SubsectionTitle>Publish as Template</SubsectionTitle>
              {templateBlockedReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="block w-full">
                      <TemplateDestinationTile selected={false} disabled onClick={() => {}} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={8}>
                    {templateBlockedReason}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <TemplateDestinationTile
                  selected={templateSelected}
                  disabled={!canPublishTemplates}
                  onClick={() => onSelectTemplateDestination?.()}
                />
              )}
              {templateRequirementsWarning && (
                <div className="mt-2 rounded-lureo-radius-lg border-lureo-border-width border-lureo-coral/50 bg-lureo-coral/10 px-3.5 py-2.5">
                  <p className="font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle">
                    {templateRequirementsWarning}
                  </p>
                </div>
              )}
              {!eligibleForTemplates && (
                <p className="mt-2 font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle">
                  Want to get paid for making templates?{' '}
                  <TextLink
                    tone="action"
                    href={TEMPLATE_INTEREST_URL}
                    {...(TEMPLATE_INTEREST_URL.startsWith('mailto:')
                      ? {}
                      : { target: '_blank', rel: 'noreferrer' })}
                    onClick={() => onRegisterTemplateInterest?.()}
                  >
                    Register your interest
                  </TextLink>
                </p>
              )}
            </div>
          )}

          {isPlaceAnywhere && !templateSelected && (
            <div className="mt-3 rounded-lureo-radius-lg border-lureo-border-width border-lureo-coral/50 bg-lureo-coral/10 px-3.5 py-2.5">
              <p className="font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle">
                This story has no anchor, so it can't be published yet. Add an anchor (Immersal scan,
                image, or map location) to publish it. You can still test it in the meantime.
              </p>
              {onTest && (
                <div className="mt-2.5">
                  <PillButton icon={DeviceMobileIcon} label="Test it" onClick={onTest} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!templateSelected && devices.includes('appless') && needsImmersalToken && (
        <div className="mt-4 shrink-0 rounded-lureo-radius-lg border-lureo-border-width border-lureo-coral/50 bg-lureo-coral/10 px-3.5 py-2.5 flex items-center justify-between gap-3">
          <p className="font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle">
            Android needs your Immersal developer token.
          </p>
          <TextButton onClick={() => onRequestImmersalToken?.()} className="shrink-0">
            Add token
          </TextButton>
        </div>
      )}
      </div>
    </TooltipProvider>
  );
}

function TemplateDestinationTile({
  selected,
  disabled,
  onClick,
}: {
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tile
      size="md"
      onClick={onClick}
      disabled={disabled}
      selected={selected}
      glyph={<SquaresFourIcon className="h-5 w-5" />}
      label="Template"
      description="Create a template for other creators to use in their own stories."
    />
  );
}
